import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ChevronLeft, Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { departmentsApi } from '../../lib/api/departmentsApi';
import DepartmentDialog from './DepartmentDialog';

const OrganizationDepartments = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);

  const fetchDepartments = () => {
    setLoading(true);
    departmentsApi
      .list()
      .then((data) => setDepartments(data.departments || []))
      .catch(() => {
        toast.error('Failed to load teams');
        setDepartments([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  const handleEdit = (dept) => {
    setEditingDepartment(dept);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDepartment(null);
  };

  const handleDelete = async (dept) => {
    if (!window.confirm(`Deactivate team "${dept.name}"? Users will no longer see it as active.`))
      return;
    try {
      await departmentsApi.delete(dept._id);
      toast.success('Team deactivated');
      fetchDepartments();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to deactivate');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Link
        to="/dashboard/organization"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 mb-6"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Organization
      </Link>
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Teams</h1>
              <p className="text-sm text-gray-500">
                Create and manage teams and their permissions.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setEditingDepartment(null);
              setDialogOpen(true);
            }}
            className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-medium text-blue-700 shadow-sm hover:bg-blue-100 transition"
          >
            <Plus className="w-4 h-4" />
            New Team
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : departments.length === 0 ? (
          <div className="p-8 text-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 mb-4">
              <Building2 className="w-7 h-7" />
            </div>
            <p className="text-gray-600 font-medium">No teams yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Create a team to assign permissions and add users.
            </p>
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              New Team
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Permissions
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {departments.map((dept) => (
                  <tr key={dept._id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-900">{dept.name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          dept.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {dept.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {(dept.permissions || []).length} permission(s)
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(dept)}
                          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                          aria-label="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {dept.status === 'active' && (
                          <button
                            type="button"
                            onClick={() => handleDelete(dept)}
                            className="rounded-lg p-2 text-gray-500 hover:bg-red-50 hover:text-red-600"
                            aria-label="Deactivate"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DepartmentDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSuccess={fetchDepartments}
        department={editingDepartment}
      />
    </div>
  );
};

export default OrganizationDepartments;
